if (!customElements.get('product-thumbnail-rail')) {
  customElements.define('product-thumbnail-rail', class ProductThumbnailRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-product-media-thumbnails]');
      this.controls = Array.from(this.querySelectorAll('[data-thumbnail-scroll]'));
      if (!this.track || !this.controls.length) return;
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.desktop = window.matchMedia('(min-width: 1200px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.dataset.enhanced = 'true';
      this.controls.forEach((control) => {
        control.hidden = false;
        control.addEventListener('click', () => {
          if (control.getAttribute('aria-disabled') !== 'true') this.scrollGroup(Number(control.dataset.thumbnailScroll));
        }, { signal });
      });
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true, signal });
      this.track.addEventListener('focusin', (event) => {
        const thumbnail = event.target.closest('[data-media-thumbnail]');
        if (thumbnail) this.revealThumbnail(thumbnail);
      }, { signal });
      this.track.addEventListener('keydown', (event) => this.onThumbnailKeydown(event), { signal });
      this.desktop.addEventListener('change', () => this.scheduleUpdate(true), { signal });
      this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate(true));
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.track);
      this.mutationObserver = new MutationObserver((records) => {
        const selectionChanged = records.some((record) => record.type === 'childList'
          || (record.attributeName === 'aria-current' && record.oldValue !== record.target.getAttribute('aria-current')));
        this.scheduleUpdate(selectionChanged);
      });
      this.mutationObserver.observe(this.track, { childList: true, subtree: true, attributes: true, attributeOldValue: true, attributeFilter: ['aria-current', 'hidden'] });
      this.scheduleUpdate(true);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.needsReveal = false;
      delete this.dataset.enhanced;
      delete this.dataset.overflow;
      delete this.dataset.canScrollUp;
      delete this.dataset.canScrollDown;
      this.controls?.forEach((control) => { control.hidden = true; });
    }

    scheduleUpdate(reveal = false) {
      this.needsReveal ||= reveal;
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        if (!this.isConnected) return;
        this.updateOverflow();
        if (this.needsReveal) {
          this.needsReveal = false;
          this.revealThumbnail(this.track.querySelector('[aria-current="true"]'));
        }
      });
    }

    updateOverflow() {
      const vertical = this.desktop.matches;
      // Compare all thumbnail content with the full rail, not the shortened
      // scrolling viewport; otherwise overflow controls cannot disappear again.
      const overflow = vertical && !this.track.hidden && this.track.scrollHeight > this.clientHeight + 1;
      this.dataset.overflow = String(overflow);
      const maximum = this.track.scrollHeight - this.track.clientHeight;
      const canScrollUp = overflow && this.track.scrollTop > 1;
      const canScrollDown = overflow && maximum - this.track.scrollTop > 1;
      this.dataset.canScrollUp = String(canScrollUp);
      this.dataset.canScrollDown = String(canScrollDown);
      this.controls.forEach((control) => {
        const available = Number(control.dataset.thumbnailScroll) < 0 ? canScrollUp : canScrollDown;
        control.setAttribute('aria-disabled', String(!available));
        control.tabIndex = available ? 0 : -1;
      });
      // If resize or a new color removes the rail controls entirely, retain
      // keyboard focus in this gallery without scrolling any page ancestor.
      if (!overflow && this.controls.includes(document.activeElement)) {
        this.track.querySelector('[aria-current="true"]')?.focus({ preventScroll: true });
      }
    }

    scrollGroup(direction) {
      if (!this.desktop.matches || this.dataset.overflow !== 'true') return;
      const first = this.track.querySelector('[data-media-thumbnail]');
      const pitch = (first?.offsetHeight || 82) + (parseFloat(getComputedStyle(this.track).gap) || 0);
      const distance = Math.max(pitch, Math.floor(this.track.clientHeight / pitch - 1) * pitch);
      this.track.scrollBy({ top: direction * distance, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    }

    revealThumbnail(thumbnail) {
      if (!thumbnail || !this.track.clientWidth) return;
      const vertical = this.desktop.matches;
      const trackRect = this.track.getBoundingClientRect();
      const thumbnailRect = thumbnail.getBoundingClientRect();
      const scroll = vertical ? this.track.scrollTop : this.track.scrollLeft;
      const start = (vertical ? thumbnailRect.top - trackRect.top : thumbnailRect.left - trackRect.left) + scroll;
      const length = vertical ? thumbnailRect.height : thumbnailRect.width;
      const viewport = vertical ? this.track.clientHeight : this.track.clientWidth;
      const inset = vertical && this.dataset.overflow === 'true' ? 18 : 2;
      let target = scroll;
      if (start < scroll + inset) target = start - inset;
      else if (start + length > scroll + viewport - inset) target = start + length - viewport + inset;
      if (Math.abs(target - scroll) > 1) {
        // Never use scrollIntoView here: it also scrolls the document when
        // customers change color or browse a gallery above their current view.
        this.track.scrollTo({ [vertical ? 'top' : 'left']: target, behavior: 'instant' });
      }
      this.scheduleUpdate();
    }

    onThumbnailKeydown(event) {
      if (!this.desktop.matches) return;
      if (event.key === 'PageUp' || event.key === 'PageDown') {
        event.preventDefault();
        this.scrollGroup(event.key === 'PageUp' ? -1 : 1);
        return;
      }
      const keys = ['ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      const thumbnails = Array.from(this.track.querySelectorAll('[data-media-thumbnail]'));
      const current = thumbnails.indexOf(event.target.closest('[data-media-thumbnail]'));
      if (current < 0) return;
      event.preventDefault();
      let next = current + (event.key === 'ArrowUp' ? -1 : 1);
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = thumbnails.length - 1;
      thumbnails[Math.max(0, Math.min(thumbnails.length - 1, next))]?.focus({ preventScroll: true });
    }
  });
}
